// value matches JS Date#getDay() (0=Sunday..6=Saturday); displayed Monday-first.
export const WEEKDAYS = [
    { value: 1, short: 'Mon', full: 'Monday' },
    { value: 2, short: 'Tue', full: 'Tuesday' },
    { value: 3, short: 'Wed', full: 'Wednesday' },
    { value: 4, short: 'Thu', full: 'Thursday' },
    { value: 5, short: 'Fri', full: 'Friday' },
    { value: 6, short: 'Sat', full: 'Saturday' },
    { value: 0, short: 'Sun', full: 'Sunday' },
];
function pad(n) {
    return String(n).padStart(2, '0');
}
/**
 * Builds a UTC cron expression firing daily (or weekly, on the given local weekday) at the
 * given local "HH:mm". EventBridge/cron-parser evaluate schedules in UTC, so the local
 * weekday+time is anchored to its next local occurrence before reading back the UTC
 * hour/minute/weekday — this keeps the day-of-week correct even when the local time falls
 * on the other side of the UTC day boundary.
 */
export function buildClockRecurrence(localTime, weekday) {
    const [hours, minutes] = localTime.split(':').map(Number);
    const ref = new Date();
    if (weekday !== undefined) {
        ref.setDate(ref.getDate() + ((weekday - ref.getDay() + 7) % 7));
    }
    ref.setHours(hours, minutes, 0, 0);
    const cronMinute = ref.getUTCMinutes();
    const cronHour = ref.getUTCHours();
    return weekday === undefined ? `${cronMinute} ${cronHour} * * *` : `${cronMinute} ${cronHour} * * ${ref.getUTCDay()}`;
}
// Matches the "M H * * *" (daily) / "M H * * D" (weekly, D=0-6) cron shape produced by
// buildClockRecurrence — the fields are UTC, so callers convert back to local time.
const DAILY_WEEKLY_CRON = /^(\d{1,2}) (\d{1,2}) \* \* (\*|[0-6])$/;
const HOURLY_DURATION = /^PT(\d+)H$/;
/** The next occurrence of a UTC "M H * * D" cron fire, as a local Date. */
function cronToLocalDate(minute, hour, dow) {
    const ref = new Date();
    ref.setUTCHours(Number(hour), Number(minute), 0, 0);
    if (dow !== '*') {
        ref.setUTCDate(ref.getUTCDate() + ((Number(dow) - ref.getUTCDay() + 7) % 7));
    }
    return ref;
}
export function formatRecurrence(recurrence) {
    if (!recurrence)
        return 'One-off';
    if (recurrence === 'P1D')
        return 'Daily';
    if (recurrence === 'P7D')
        return 'Weekly';
    if (recurrence === 'PT1H')
        return 'Every hour';
    if (recurrence === 'PT30M')
        return 'Every 30 minutes';
    const hourlyMatch = recurrence.match(HOURLY_DURATION);
    if (hourlyMatch)
        return `Every ${hourlyMatch[1]} hours`;
    const match = recurrence.match(DAILY_WEEKLY_CRON);
    if (match) {
        const [, minute, hour, dow] = match;
        const ref = cronToLocalDate(minute, hour, dow);
        const time = ref.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        return dow === '*' ? `Daily at ${time}` : `Weekly on ${ref.toLocaleDateString(undefined, { weekday: 'long' })} at ${time}`;
    }
    return recurrence;
}
/** Inverse of `draftToRecurrence` — anything that isn't one of the picker's own shapes opens
 * as a custom expression so it round-trips untouched. A one-off (no recurrence) opens as daily
 * at the current time, since a recurrence can only be set, not cleared, through PATCH. */
export function parseRecurrence(recurrence) {
    if (!recurrence) {
        const now = new Date();
        return { kind: 'daily', time: `${pad(now.getHours())}:${pad(now.getMinutes())}` };
    }
    const hourlyMatch = recurrence.match(HOURLY_DURATION);
    if (hourlyMatch)
        return { kind: 'hourly', interval: Number(hourlyMatch[1]) };
    const match = recurrence.match(DAILY_WEEKLY_CRON);
    if (match) {
        const [, minute, hour, dow] = match;
        const ref = cronToLocalDate(minute, hour, dow);
        const time = `${pad(ref.getHours())}:${pad(ref.getMinutes())}`;
        return dow === '*' ? { kind: 'daily', time } : { kind: 'weekly', weekday: ref.getDay(), time };
    }
    return { kind: 'custom', expression: recurrence };
}
/** `null` while the draft is incomplete or invalid. */
export function draftToRecurrence(draft) {
    switch (draft.kind) {
        case 'hourly':
            return Number.isInteger(draft.interval) && draft.interval >= 1 ? `PT${draft.interval}H` : null;
        case 'daily':
            return draft.time ? buildClockRecurrence(draft.time) : null;
        case 'weekly':
            return draft.time ? buildClockRecurrence(draft.time, draft.weekday) : null;
        case 'custom':
            return draft.expression.trim() || null;
    }
}
